import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { GetPageComponentsResponse } from "../../../(custom-header)/winners-template/ComponentsMapper";
import { JudgesFaqsResponse, JudgesResponse } from "../../../(custom-header)/winners-template/modals/types";
import { AppCurrencies } from "../../../common";
import { PrizeTicketProps } from "../../../components/PrizeTicketSelector/types";
import { CompetitionPrizeTypes } from "../../../(general-header)/management-subscription/types";
import { WinnersData } from "../../../(general-header)/winners/types";
import { generateMockWinnersPage } from "./winners.mocks";
import { InstantWinWinnersParams, InstantWinWinnersResponse } from "./winners.types";
import { divideEtImperaComponents, extractFilenameFromContentDisposition } from "./winners.utils";

export type { InstantWinWinnersParams, InstantWinWinnersResponse } from "./winners.types";
export type { WinnersData } from "../../../(general-header)/winners/types";

export const winnersApi = createApi({
  reducerPath: "winnersApi",
  baseQuery: fetchBaseQuery({ baseUrl: process.env.NEXT_PUBLIC_HOST }),
  tagTypes: ["PageComponents", "DCExtraPrizesComponents", "InstantWinWinners", "WinnersList", "PrizeStrapline"],
  endpoints: build => ({
    getPageComponents: build.query<GetPageComponentsResponse, { path: string; isMobile?: boolean }>({
      query: ({ path }) => ({
        url: "/umbraco/botb/winnersresult/getpagecomponents",
        params: { path },
      }),
      providesTags: ["PageComponents"],
      transformResponse: (baseQueryReturnValue: GetPageComponentsResponse, meta, arg) => {
        return {
          ...baseQueryReturnValue,
          PageComponents: baseQueryReturnValue.PageComponents.flatMap(c =>
            divideEtImperaComponents(
              c,
              baseQueryReturnValue.template,
              arg.isMobile,
              baseQueryReturnValue.PageComponents
            )
          ),
        };
      },
    }),
    getDCExtraPrizesComponents: build.query<GetPageComponentsResponse, string>({
      query: path => ({
        url: "/umbraco/botb/winnersresult/getdcextraprizescomponent",
        params: {
          path,
        },
      }),
      providesTags: ["DCExtraPrizesComponents"],
      transformResponse: (baseQueryReturnValue: GetPageComponentsResponse) => {
        return {
          ...baseQueryReturnValue,
          PageComponents: baseQueryReturnValue.PageComponents.flatMap(c =>
            divideEtImperaComponents(c, baseQueryReturnValue.template, undefined, baseQueryReturnValue.PageComponents)
          ),
        };
      },
    }),
    getPrizeData: build.query<PrizeTicketProps, number>({
      query: feelingLuckyPrizeId => ({
        url: "/umbraco/surface/PrizeTicketSurface/GetPriceTicketFromContentNodeId",
        params: {
          contentNodeId: feelingLuckyPrizeId,
        },
      }),
    }),
    downloadImage: build.mutation<
      string,
      {
        competitionPictureGuid: string;
        includeWinner: boolean;
        includeJudged: boolean;
        includeUserClosest: boolean;
        includeUserEntries: boolean;
        FullSizeImage: boolean;
      }
    >({
      query: params => {
        const urlSearchParams = new URLSearchParams();
        urlSearchParams.append("competitionPictureGuid", params.competitionPictureGuid);
        urlSearchParams.append("includeWinner", params.includeWinner.toString());
        urlSearchParams.append("includeJudged", params.includeJudged.toString());
        urlSearchParams.append("includeUserClosest", params.includeUserClosest.toString());
        urlSearchParams.append("includeUserEntries", params.includeUserEntries.toString());
        urlSearchParams.append("FullSizeImage", params.FullSizeImage.toString());

        return {
          url: "/service/spottheball/downloadimage",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          method: "POST",
          body: urlSearchParams.toString(),
          responseHandler: async response => {
            const hiddenElement = document.createElement("a");
            const url = window.URL || window.webkitURL;
            const blobImage = url.createObjectURL(await response.blob());
            hiddenElement.href = blobImage;
            hiddenElement.target = "_blank";
            hiddenElement.download = extractFilenameFromContentDisposition(
              response.headers.get("Content-Disposition") || "file.jpg"
            );
            hiddenElement.click();
            hiddenElement.remove();
          },
          cache: "no-cache",
        };
      },
    }),
    getCurrencies: build.query<AppCurrencies, void>({
      query: () => ({
        url: "/umbraco/botb/CurrencySelector/GetCurrencies",
        method: "GET",
        credentials: "include",
      }),
    }),
    getDCJudges: build.query<JudgesResponse, void>({
      query: () => ({
        url: "/umbraco/botb/winnersresult/getdcjudges",
        method: "GET",
      }),
    }),
    getJudgesFAQ: build.query<JudgesFaqsResponse, void>({
      query: () => ({
        url: "/umbraco/botb/winnersresult/getbotbfaqs",
        method: "GET",
      }),
    }),
    getInstantWinWinners: build.query<InstantWinWinnersResponse, InstantWinWinnersParams>({
      query: ({ url, competitionId, page = 0, pageSize = 20 }) => ({
        url,
        params: {
          competitionid: competitionId,
          page,
          pageSize,
        },
      }),
      transformResponse: (result: InstantWinWinnersResponse, _meta, arg) => {
        if (!arg.useMocks) return result;
        return generateMockWinnersPage(arg.page ?? 0, arg.pageSize ?? 20);
      },
      providesTags: ["InstantWinWinners"],
    }),
    getWinnersList: build.query<WinnersData, void>({
      query: () => ({ url: "/umbraco/surface/WinnersSurface/GetWinners" }),
      transformResponse: (res: { data: WinnersData }) => res.data,
      providesTags: ["WinnersList"],
    }),
    getPrizeStraplineTexts: build.query<CompetitionPrizeTypes, void>({
      query: () => ({ url: "/umbraco/00000001/app/GetPrizeStraplineTexts" }),
      providesTags: ["PrizeStrapline"],
    }),
  }),
});

export const {
  useGetPageComponentsQuery,
  useGetDCExtraPrizesComponentsQuery,
  useGetPrizeDataQuery,
  useDownloadImageMutation,
  useGetCurrenciesQuery,
  useGetDCJudgesQuery,
  useGetJudgesFAQQuery,
  useGetInstantWinWinnersQuery,
  useGetWinnersListQuery,
  useGetPrizeStraplineTextsQuery,
} = winnersApi;
